import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SKIP_RETRY } from '@nx-ratenow/core/errors/http-errors';
import { iif, Observable, of, throwError } from 'rxjs';
import { delay, mergeMap, retryWhen } from 'rxjs/operators';

const statusToRetry = [502, 503];

@Injectable()
export class RetryInterceptor implements HttpInterceptor {
  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (request.context.get(SKIP_RETRY)) return next.handle(request);

    return next.handle(request).pipe(retryWithBackoff(2, 500, 500));
  }
}

const DEFAULT_MAX_RETRIES = 2;
const DEFAULT_BACKOFF = 1000; //ms

const getErrorMessage = (maxRetry: number) => `Tried to load Resource over XHR for ${maxRetry} times without success. Giving up.`;

/**
 * This observable is a variation of the retryWhen that allow to define max number of retries
 * and increase the amount of delay each retry depending on the retries already carried out.
 * @param maxRetry max number of retries
 * @param delayMs delay in miliseconds after each error
 * @param backoff backoff * number of retries added to the delayMs
 * @returns final observable resolution: error after maxRetry, otherwise a valid HTTPResponse
 */
export function retryWithBackoff(maxRetry = DEFAULT_MAX_RETRIES, delayMs: number, backoff = DEFAULT_BACKOFF) {
  let retries = maxRetry;
  return (src: Observable<any>) =>
    src.pipe(
      retryWhen((errors: Observable<any>) =>
        errors.pipe(
          mergeMap((error) =>
            iif(
              () => statusToRetry.includes(error.status),
              //Status code is in statusToRetry apply retry policy
              iif(() => retries-- > 0, of(error).pipe(delay(delayMs + (maxRetry - retries) * backoff)), throwError(getErrorMessage(maxRetry))),
              //Status code is not included in "statusToRetry" return the error to application
              throwError(error)
            )
          )
        )
      )
    );
}
